import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import {
  addResearchBrandSource,
  createResearchBrand,
  deleteResearchBrand,
  deleteResearchBrandSource,
  listResearchBrands,
  unlinkResearchBrandCustomer,
} from "../../api/Skills/skillAgentResearchBrandsApi";
import {
  pageFieldSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";

/**
 * Gestionar catálogo Marca+Web del cliente (BBDD).
 */
export default function SkillAgentResearchCatalogDialog({
  open,
  onClose,
  customerId,
  customerName,
  onChanged,
}) {
  const { t } = useTranslation();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [draftName, setDraftName] = useState("");
  const [draftUrl, setDraftUrl] = useState("");
  const [draftLabel, setDraftLabel] = useState("");
  const [expandId, setExpandId] = useState(null);
  const [sourceDraft, setSourceDraft] = useState({ url: "", label: "" });

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setError("");
    try {
      const data = await listResearchBrands({ customerId });
      setBrands(Array.isArray(data?.brands) ? data.brands : []);
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.researchCatalog.loadError"),
      );
      setBrands([]);
    } finally {
      setLoading(false);
    }
  }, [customerId, t]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleCreate = async () => {
    const name = draftName.trim();
    const url = draftUrl.trim();
    if (!name) {
      setError(t("skillAgent.researchCatalog.brandNameRequired"));
      return;
    }
    if (url && !/^https?:\/\//i.test(url)) {
      setError(t("skillAgent.researchCatalog.urlMustBeHttp"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      await createResearchBrand({
        name,
        customerId: Number(customerId),
        sources: url
          ? [{ url, label: draftLabel.trim() || null, defaultSelected: true }]
          : [],
      });
      setDraftName("");
      setDraftUrl("");
      setDraftLabel("");
      setOkMsg(t("skillAgent.researchCatalog.brandAdded"));
      await load();
      onChanged?.();
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.researchCatalog.createError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async (brand) => {
    if (!brand?.dbId) return;
    setBusy(true);
    setError("");
    try {
      await unlinkResearchBrandCustomer(brand.dbId, Number(customerId));
      setOkMsg(
        t("skillAgent.researchCatalog.unlinked", { name: brand.name }),
      );
      await load();
      onChanged?.();
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.researchCatalog.unlinkError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteBrand = async (brand) => {
    if (!brand?.dbId) return;
    if (
      !window.confirm(
        t("skillAgent.researchCatalog.confirmDeleteBrand", {
          name: brand.name,
        }),
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await deleteResearchBrand(brand.dbId);
      setOkMsg(t("skillAgent.researchCatalog.brandDeleted"));
      await load();
      onChanged?.();
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.researchCatalog.deleteError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleAddSource = async (brand) => {
    const url = sourceDraft.url.trim();
    if (!brand?.dbId || !url) return;
    if (!/^https?:\/\//i.test(url)) {
      setError(t("skillAgent.researchCatalog.urlMustBeHttp"));
      return;
    }
    setBusy(true);
    try {
      await addResearchBrandSource(brand.dbId, {
        url,
        label: sourceDraft.label.trim() || null,
        defaultSelected: true,
      });
      setSourceDraft({ url: "", label: "" });
      await load();
      onChanged?.();
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.researchCatalog.addWebError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteSource = async (brand, source) => {
    if (!brand?.dbId || !source?.id) {
      setError(t("skillAgent.researchCatalog.sourceNoId"));
      return;
    }
    setBusy(true);
    try {
      await deleteResearchBrandSource(brand.dbId, source.id);
      await load();
      onChanged?.();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.researchCatalog.deleteWebError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const customerTitle =
    customerName ||
    t("skillAgent.researchCatalog.clientFallback", { id: customerId });

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose?.()}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle fontWeight={800}>
        {t("skillAgent.researchCatalog.title", { name: customerTitle })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {t("skillAgent.researchCatalog.intro")}
          </Typography>
          {error && (
            <Alert severity="error" onClose={() => setError("")}>
              {error}
            </Alert>
          )}
          {okMsg && (
            <Alert severity="success" onClose={() => setOkMsg("")}>
              {okMsg}
            </Alert>
          )}

          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 1.25,
            }}
          >
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
              {t("skillAgent.researchCatalog.newBrandHeading")}
            </Typography>
            <Stack spacing={1}>
              <TextField
                size="small"
                label={t("skillAgent.researchCatalog.nameLabel")}
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                fullWidth
                sx={pageFieldSx}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  size="small"
                  label={t("skillAgent.researchCatalog.urlOptional")}
                  value={draftUrl}
                  onChange={(e) => setDraftUrl(e.target.value)}
                  fullWidth
                  sx={pageFieldSx}
                />
                <TextField
                  size="small"
                  label={t("skillAgent.researchCatalog.labelField")}
                  value={draftLabel}
                  onChange={(e) => setDraftLabel(e.target.value)}
                  sx={{ width: { xs: "100%", sm: 140 }, ...pageFieldSx }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  disabled={busy || !draftName.trim()}
                  onClick={handleCreate}
                  sx={{ ...pagePrimaryButtonSx, textTransform: "none" }}
                >
                  {t("skillAgent.researchCatalog.addBrand")}
                </Button>
              </Stack>
            </Stack>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <Stack spacing={0.75} sx={{ maxHeight: 360, overflow: "auto" }}>
              {brands.map((b) => {
                const sourceCount = (b.sources || []).length;
                const webWord =
                  sourceCount === 1
                    ? t("skillAgent.researchCatalog.webSingular")
                    : t("skillAgent.researchCatalog.webPlural");
                return (
                  <Box
                    key={b.id || b.dbId}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      px: 1.25,
                      py: 0.75,
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700}>
                          {b.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {sourceCount} {webWord} · {b.id}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <Button
                          size="small"
                          sx={{ textTransform: "none" }}
                          onClick={() =>
                            setExpandId(expandId === b.dbId ? null : b.dbId)
                          }
                        >
                          {t("skillAgent.researchCatalog.websButton")}
                        </Button>
                        <Button
                          size="small"
                          color="warning"
                          sx={{ textTransform: "none" }}
                          disabled={busy || !b.dbId}
                          onClick={() => handleUnlink(b)}
                        >
                          {t("skillAgent.researchCatalog.unlinkFromClient")}
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={busy || !b.dbId}
                          onClick={() => handleDeleteBrand(b)}
                          aria-label={t(
                            "skillAgent.researchCatalog.ariaDeleteBrand",
                          )}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                    {expandId === b.dbId && (
                      <Box sx={{ mt: 1, pl: 0.5 }}>
                        {(b.sources || []).map((s) => (
                          <Stack
                            key={s.id || s.url}
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                            sx={{ mb: 0.35 }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ wordBreak: "break-all", flex: 1 }}
                            >
                              {s.defaultSelected ? "● " : "○ "}
                              {s.label || t("skillAgent.researchCatalog.urlFallback")}{" "}
                              — {s.url}
                            </Typography>
                            {s.id ? (
                              <IconButton
                                size="small"
                                disabled={busy}
                                onClick={() => handleDeleteSource(b, s)}
                                aria-label={t(
                                  "skillAgent.researchCatalog.ariaRemoveWeb",
                                )}
                              >
                                <DeleteOutlineIcon fontSize="inherit" />
                              </IconButton>
                            ) : null}
                          </Stack>
                        ))}
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={0.75}
                          sx={{ mt: 1 }}
                        >
                          <TextField
                            size="small"
                            label={t("skillAgent.researchCatalog.newUrl")}
                            value={sourceDraft.url}
                            onChange={(e) =>
                              setSourceDraft((p) => ({
                                ...p,
                                url: e.target.value,
                              }))
                            }
                            fullWidth
                          />
                          <TextField
                            size="small"
                            label={t("skillAgent.researchCatalog.labelField")}
                            value={sourceDraft.label}
                            onChange={(e) =>
                              setSourceDraft((p) => ({
                                ...p,
                                label: e.target.value,
                              }))
                            }
                            sx={{ width: { xs: "100%", sm: 120 } }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={busy || !sourceDraft.url.trim()}
                            onClick={() => handleAddSource(b)}
                            sx={{ textTransform: "none" }}
                          >
                            {t("skillAgent.researchCatalog.addWeb")}
                          </Button>
                        </Stack>
                      </Box>
                    )}
                  </Box>
                );
              })}
              {!brands.length && (
                <Typography variant="caption" color="text.secondary">
                  {t("skillAgent.researchCatalog.emptyBrands")}
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          {t("skillAgent.common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
